import styled, { keyframes, css, createGlobalStyle } from 'styled-components'

export const lightTheme = {
  background: '#ffbf40',
  text: '#1a1a1a',
  textSecondary: 'rgba(0,0,0,0.4)',
  glass: 'rgba(255, 255, 255, 0.1)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  cardBg: 'rgba(255, 255, 255, 0.05)',
  modalBg: 'rgba(255, 255, 255, 0.1)',
}

export const darkTheme = {
  background: '#0d0d0d',
  text: '#ffffff',
  textSecondary: 'rgba(255,255,255,0.4)',
  glass: 'rgba(255, 255, 255, 0.05)',
  glassBorder: 'rgba(255, 255, 255, 0.1)',
  cardBg: 'rgba(255, 255, 255, 0.03)',
  modalBg: 'rgba(0, 0, 0, 0.4)',
}

export const GlobalStyle = createGlobalStyle`
  html {
    background-color: ${props => props.theme.background};
  }
  html, body {
    margin: 0;
    padding: 0;
    overscroll-behavior: none;
    background-color: ${props => props.theme.background};
    transition: background-color 0.5s ease;
  }
`

export const fade = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

export const FadeIn = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background: ${props => props.theme.background};
  animation: ${fade} 4s normal forwards ease-in-out;
`

export const Container = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: ${props => props.theme.text};
  & h1 {
    padding: 0;
    margin: 0;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 400;
    font-size: clamp(3rem, 18vmin, 18rem);
    line-height: 0.9em;
    color: ${props => props.theme.text};
    @media (max-width: 768px) {
      font-size: clamp(2.5rem, 14vw, 6rem);
    }
    @media (max-height: 500px) {
      font-size: clamp(2rem, 12vmin, 6rem);
    }
  }
  & .tablet-break {
    display: none;
    @media (max-width: 1024px) {
      display: block;
    }
  }
  & .mobile-break {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }
  & .desktop-break {
    display: block;
    @media (max-width: 768px) {
      display: none;
    }
  }
`

export const TopLeft = styled.div`
  position: absolute;
  top: 5vmin;
  left: 5vmin;
  pointer-events: auto;
  z-index: 10;
  width: auto;
  max-width: 90vw;
  line-height: 1;
  @media (max-height: 500px) {
    top: 3vmin;
  }
`

export const BottomLeft = styled.div`
  position: absolute;
  bottom: 5vmin;
  left: 5vmin;
  width: auto;
  max-width: 40%;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  @media (max-width: 768px) {
    width: 70%;
    max-width: none;
    bottom: 25vh;
  }
  @media (max-height: 500px) {
    bottom: 3vmin;
  }
`

export const BottomRight = styled.div`
  position: absolute;
  bottom: 5vmin;
  right: 5vmin;
  width: 35ch;
  max-width: 40%;
  line-height: 1.2em;
  letter-spacing: -0.01em;
  text-align: right;
  @media (max-width: 768px) {
    width: 90%;
    max-width: none;
    text-align: left;
    left: 5vmin;
    bottom: 5vmin;
  }
  @media (max-height: 500px) {
    bottom: 3vmin;
    line-height: 1em;
  }
`

export const Hamburger = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 5vmin;
  right: 5vmin;
  z-index: 1001;
  cursor: pointer;
  pointer-events: auto;
  gap: 6px;

  & > div {
    position: relative;
    width: 24px;
    height: 2px;
    background: ${props => props.theme.text};
    transition: all 0.3s ease;
  }
`

export const ThemeSwitchLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 0.6em;
  height: 0.3em;
  background-color: ${props => props.$isDark ? 'rgb(104, 104, 104)' : 'rgb(199, 199, 199)'};
  border-radius: 1em;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-left: 0.15em;
  vertical-align: middle;
  transform: translateY(-0.13em);
  pointer-events: auto;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    height: 0.3em;
    width: 0.3em;
    left: ${props => props.$isDark ? '0.3em' : '0px'};
    background: conic-gradient(rgb(104, 104, 104), white, rgb(104, 104, 104), white, rgb(104, 104, 104));
    border-radius: 50%;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0.05em 0.05em 0.1em rgba(0, 0, 0, 0.3);
  }
`

export const HiddenCheckbox = styled.input`
  display: none;
`

export const MenuBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 80;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? 'all' : 'none')};
  transition: opacity 0.5s ease;
`

export const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: auto;
  width: 33vw;
  min-width: 300px;
  height: 100%;
  background: ${props => props.theme.modalBg};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: -10px 0 30px rgba(0,0,0,0.1);
  transform: translateX(${(props) => (props.$isOpen ? '0%' : '100%')});
  pointer-events: ${(props) => (props.$isOpen ? 'all' : 'none')};
  transition: transform 0.5s cubic-bezier(0.77, 0, 0.175, 1);

  @media (max-width: 768px) {
    width: 80vw;
  }
`

export const MenuNav = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2rem;
  width: 100%;
  padding: 0 3rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 2rem;
  }
`

export const MenuItem = styled.a`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2.5rem;
  font-weight: 300;
  color: ${props => props.theme.text};
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.3s ease, color 0.3s ease;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  line-height: 1.2;
  
  &:hover {
    color: ${props => props.theme.textSecondary};
    transform: translateX(10px);
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
    gap: 1rem;
  }
`

export const MenuNumber = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: ${props => props.theme.textSecondary};
  margin-top: 0.2em; /* Align with top of text */
  flex-shrink: 0;
`

export const LeftMiddle = styled.div`
  position: fixed;
  bottom: 50%;
  right: 5vw;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 400;
  line-height: 1em;
  letter-spacing: -0.01em;
  font-size: 12px;
  transform: rotate(90deg) translate3d(50%, 0, 0);
  transform-origin: 100% 50%;
  z-index: 1000;
  color: ${props => props.theme.text};
  transition: opacity 0.3s ease;
  
  input {
    background: #000;
    &::-webkit-slider-thumb {
      background: #000;
    }
  }
`

export const ScrollWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  pointer-events: all;
  scroll-behavior: smooth;
`

export const HeroSection = styled.div`
  min-height: 100dvh;
  position: relative;
  width: 100%;
  pointer-events: none; /* Elements inside need pointer-events: all */
`

export const Section = styled.section`
  min-height: 100dvh;
  width: 100%;
  background: transparent;
  padding: 6rem 2rem;
  position: relative;
  pointer-events: all;
`

export const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 4rem;
  color: ${props => props.theme.text};
  margin-bottom: 4rem;
  text-align: center;
  line-height: 1;
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`

export const SectionSubtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.9rem;
  color: ${props => props.theme.textSecondary};
  text-align: center;
  margin-top: -3rem;
  margin-bottom: 5rem;
  letter-spacing: 0.05em;
`


export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    padding: 0 1rem;
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

export const Card = styled.div`
  background: ${props => props.theme.cardBg};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.glassBorder};
  padding: 2rem;
  border-radius: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 1;

  &:hover {
    background: ${props => props.theme.glass};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    z-index: 2;
  }
`

export const CopyButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.9rem;
  opacity: 0.6;
  transition: opacity 0.2s;
  padding: 0;
  margin-top: 1rem;
  align-self: flex-start;
  z-index: 10;

  &:hover {
    opacity: 1;
  }
`

export const CardTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.8rem;
  font-weight: 400;
  color: ${props => props.theme.text};
  margin: 0 0 1rem 0;
  line-height: 1.2;
`

export const CardNumber = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  letter-spacing: 0.1em;
  margin-bottom: -0.5rem;
`

export const CardText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  line-height: 1.6;
  margin: 0;
`

export const CardImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.05);
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  ${Card}:hover & {
    transform: scale(1.05);
  }
`

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 5000;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? 'all' : 'none')};
  transition: opacity 0.3s ease;
`

export const ModalContent = styled.div`
  background: ${props => props.theme.modalBg};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${props => props.theme.glassBorder};
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 24px;
  padding: 3rem;
  position: relative;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  transform: scale(${(props) => (props.$isOpen ? 1 : 0.9)});
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`

export const ModalImage = styled.img`
  width: 100%;
  max-height: 60vh;
  height: auto;
  border-radius: 12px;
  object-fit: contain;
  margin-bottom: 0.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  background: rgba(0, 0, 0, 0.05);
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`


export const CloseModalButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.2rem;
  cursor: pointer;
  color: #ffffff;
  z-index: 100;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(0, 0, 0, 0.5);
    transform: scale(1.1) rotate(90deg);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:active {
    transform: scale(0.9);
  }
`

export const SourceLink = styled.a`
  background: transparent;
  border: 1px solid ${props => props.theme.glassBorder};
  border-radius: 8px;
  cursor: pointer;
  color: ${props => props.theme.text};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.9rem;
  text-decoration: none;
  padding: 0.5rem 1rem;
  margin-top: 1rem;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.cardBg};
    border-color: ${props => props.theme.textSecondary};
  }
`

export const LoadMoreButton = styled.button`
  background: ${props => props.theme.glass};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.glassBorder};
  border-radius: 30px;
  padding: 1rem 2.5rem;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${props => props.theme.text};
  cursor: pointer;
  margin: 4rem auto 0;
  display: block;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  &:hover {
    background: ${props => props.theme.cardBg};
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.textSecondary};
  }

  &:active {
    transform: translateY(-2px);
  }
`

export const AvatarWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
  margin-bottom: 2rem;

  &:hover .speech-bubble {
    opacity: 1;
    transform: translate(0, -5px) rotate(-5deg);
  }
`

export const SpeechBubble = styled.div`
  position: absolute;
  top: -20px;
  left: -40px;
  transform: translate(0, 5px) rotate(-5deg);
  background: #ffffff;
  color: #1a1a1a;
  padding: 0.8rem 1.4rem;
  border-radius: 18px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  z-index: 100;
  border: 2px solid rgba(0, 0, 0, 0.05);

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 30px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 10px solid #ffffff;
  }
`

export const AvatarImage = styled.img`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center 25%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 4px solid rgba(255, 255, 255, 0.3);
  display: block;
`

export const SubHeading = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.5rem, 4vmin, 3rem);
  font-weight: 300;
  margin-top: 1rem;
  line-height: 1.2em;
  color: ${props => props.theme.text};
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-top: 1rem;
  }
  @media (max-height: 500px) {
    font-size: 1.2rem;
    margin-top: 0.5rem;
  }
`
export const ComingSoonSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 400px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-top: 2rem;
  padding: 2rem;
  text-align: center;
  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
    margin: 0 5vw;
    padding: 3rem 1.5rem;
  }
`

export const ComingSoonText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1rem, 5vw, 1.4rem);
  color: ${props => props.theme.textSecondary};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  max-width: 20ch;
  line-height: 1.4;
`

export const KakaoButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: #FEE500;
  color: #1a1a1a;
  border: none;
  border-radius: 12px;
  padding: 1rem 2.5rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 15px rgba(254, 229, 0, 0.2);

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 8px 25px rgba(254, 229, 0, 0.4);
  }

  &:active {
    transform: translateY(-2px);
  }

  svg {
    font-size: 2.2rem;
    flex-shrink: 0;
  }
`

export const Footer = styled.footer`
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  color: ${props => props.theme.text};
  font-family: 'Space Grotesk', sans-serif;
  border-top: 1px solid ${props => props.theme.glassBorder};
  margin-top: 4rem;
  background: ${props => props.theme.cardBg};
`

export const FooterLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
`

export const Tag = styled.span`
  background: #ff4757;
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  display: inline-block;
`

export const TrendyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const TrendyCard = styled(Card)`
  min-height: 350px;
  background: ${props => props.$isLocked ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${props.$bg})` : props.theme.cardBg};
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  border: 1px solid ${props => props.$isLocked ? 'rgba(255,255,255,0.1)' : props.theme.glassBorder};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    backdrop-filter: ${props => props.$isLocked ? 'blur(12px) grayscale(0.5)' : 'none'};
    -webkit-backdrop-filter: ${props => props.$isLocked ? 'blur(12px) grayscale(0.5)' : 'none'};
    z-index: 0;
  }

  & * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    transform: ${props => props.$isLocked ? 'translateY(-2px)' : 'translateY(-5px)'};
  }
`

export const SuggestCard = styled(Card)`
  background: ${props => props.theme.cardBg};
  border: 1px dashed ${props => props.theme.glassBorder};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 3rem;
  grid-column: 1 / -1;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-style: solid;
  }

  @media (max-width: 900px) {
    grid-column: auto;
  }
`

export const ReleaseBadge = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: ${props => props.theme.text};
  color: ${props => props.theme.background};
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  z-index: 2;
`

export const LockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #fff;
  z-index: 1;
  text-align: center;
  padding: 2rem;

  svg {
    opacity: 0.8;
  }
`

export const PremiumCard = styled(Card)`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 107, 107, 0.1));
  border: 1px solid rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "PREMIUM";
    position: absolute;
    top: 12px;
    right: -30px;
    background: #ffd700;
    color: #000;
    font-size: 0.7rem;
    font-weight: 800;
    padding: 4px 40px;
    transform: rotate(45deg);
  }

  &:hover {
    border-color: #ffd700;
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.25), rgba(255, 107, 107, 0.2));
  }
`

export const VideoScrollContainer = styled.div`
  display: flex;
  gap: 2rem;
  overflow-x: auto;
  padding: 2rem 0;
  cursor: grab;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    padding: 1rem 10vw;
  }
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.text};
    border-radius: 10px;
    opacity: 0.3;
  }

  &:active {
    cursor: grabbing;
  }
`

export const VideoCard = styled.div`
  flex: 0 0 600px;
  border-radius: 20px;
  overflow: hidden;
  background: ${props => props.theme.cardBg};
  border: 1px solid ${props => props.theme.glassBorder};
  transition: all 0.3s ease;
  aspect-ratio: 16 / 9;
  scroll-snap-align: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
    border-color: ${props => props.theme.text};
  }

  @media (max-width: 768px) {
    flex: 0 0 80vw;
  }
`

export const VideoNavContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2rem;
`

export const NavButton = styled.button`
  background: ${props => props.theme.cardBg};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.glassBorder};
  color: ${props => props.theme.text};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);

  &:hover {
    transform: scale(1.1);
    background: ${props => props.theme.text};
    color: ${props => props.theme.background};
    border-color: ${props => props.theme.text};
  }

  &:active {
    transform: scale(0.95);
  }
`

export const VideoFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`


